import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DateFormatorDD, getFrameworkUrl } from "../../utils/helper";
import { Card, CardBody, CardHeader, Image } from "@nextui-org/react";
import Completed from "../../Images/done.svg";
import incompleteIcon from "../../Images/incompleteIcon.svg";
import lastUpdate from "../../Images/lastUpdate.svg";
import { fetchMetricInfo } from "../../utils/helper";
import { capitalize } from "../../controllers/APIController";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import { PageLoader } from "../../components/Loader";
import useGetOrgFramework from "../../hooks/useGetOrgFramework";
import { categoryNameList } from '../../utils/constant';

/**
 * Metrics Component
 * Main page that displays an overview of ESG metrics across different categories
 * Shows total metrics, completed metrics, pending metrics, and last update information
 * Provides navigation to detailed metric views for each category
 */
const Metrics = () => {
  // State for loading status and metric information
  const [isLoading, setIsLoading] = useState(true);
  const [metricInfo, setMetricInfo] = useState({
    environment: {},
    social: {},
    governance: {},
    survey: {},
  });

  // Get category names from constants
  const metricNameList = categoryNameList;

  // Navigation and routing hooks
  const navigate = useNavigate();
  const { _orgId: currentOrgId } = useParams();
  
  // Get user and organization information from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;

  // Fetch organization framework data
  const { frameworkList, isLoading: isFrameworkLoading } =
    useGetOrgFramework(orgInfo);

  // Get framework URL for API calls
  const frameworkUrl = getFrameworkUrl(frameworkList);

  /**
   * Fetch metric information when framework data is available
   * Updates metric info state and loading status
   */
  useEffect(() => {
    if (isFrameworkLoading) return;
    
    fetchMetricInfo(metricNameList, orgInfo, frameworkUrl)
      .then((data) => {
        setMetricInfo(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching metric info:", error);
        setIsLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFrameworkLoading, orgInfo]);

  /**
   * Navigate to detailed metric view for a specific category
   * @param {string} name - Category name to navigate to
   */
  const navigateToCategory = (name) => {
    const categoryId = name.replaceAll(" ", "-");
    navigate(`/${currentOrgId}/metrics/${categoryId}`);
  };

  // Show PageLoader for initial page load
  if (isLoading) {
    return (
      <BreadCrumbsPage>
        <PageLoader
          loadingText="Loading ESG Metrics..."
          description="Fetching metric information and organization framework"
          variant="default"
        />
      </BreadCrumbsPage>
    );
  }

  return (
    <BreadCrumbsPage>
      <>
        {/* Page Header */}
        <div className="text-3xl font-medium">ESG Metrics </div>
        <div className="mt-8">
          Here are a list of your ESG Metrics that are calculated to generate
          your ESG report. These metrics will help Audyt to generate a report.
        </div>

        {/* Render metric category cards */}
        {metricNameList.map((categoryName) => {
              const metric = metricInfo[categoryName] || {};
              const totalCount = metric.totalCount || 0;
              const completedCount = metric.CompletedCount || 0;
              const pendingCount = metric.InCompleteCount || 0;
              const lastUpdatedAt = metric?.lastUpdatedAt
                ? DateFormatorDD(metric.lastUpdatedAt)
                : "--";

              return (
                <div
                  key={categoryName}
                  className=""
                  onClick={() => navigateToCategory(categoryName)}
                >
                  <Card
                    className="bg-[#F8F8F8] h-[160px] w-full mt-8 p-4 cursor-pointer"
                    shadow="none"
                  >
                    <CardHeader>
                      <div className="text-xl font-medium">
                        {capitalize(categoryName)} Metrics
                      </div>
                    </CardHeader>
                    <CardBody>
                      {/* Metric statistics display */}
                      <div className="flex gap-16">
                        {/* Total Metrics Count */}
                        <div>
                          <div className="flex items-center gap-4">
                            <Image
                              width={50}
                              alt="Total metrics icon"
                              src={Completed}
                            />
                            <div>
                              <div className="text-sm">Total Metrics</div>
                              <div className="font-bold">{totalCount}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Completed Metrics Count */}
                        <div>
                          <div className="flex items-center gap-4">
                            <Image
                              width={50}
                              alt="Completed metrics icon"
                              src={Completed}
                            />
                            <div>
                              <div className="text-sm">
                                Total Completed Metrics
                              </div>
                              <div className="font-semibold">
                                {completedCount}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Pending Metrics Count */}
                        <div>
                          <div className="flex items-center gap-4">
                            <Image
                              width={50}
                              alt="Pending metrics icon"
                              src={incompleteIcon}
                            />
                            <div>
                              <div className="text-sm">
                                Total Pending Metrics
                              </div>
                              <div className="font-semibold">
                                {pendingCount}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Last Update Information */}
                        <div className="flex items-center gap-4">
                          <Image
                            width={50}
                            alt="Last update icon"
                            src={lastUpdate}
                          />
                          <div>
                            <div className="text-sm">Last Updated at</div>
                            <div className="font-semibold">{lastUpdatedAt}</div>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              );
            })}
        </>
      </BreadCrumbsPage>
    );
  };

export default Metrics;
